import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '@/context/LanguageContext';
import { useSettings } from '@/context/SettingsContext';
import { 
  Facebook, 
  Instagram, 
  Linkedin, 
  MapPin, 
  Phone, 
  Mail,
  ExternalLink,
  Star
} from 'lucide-react';

// Fallback Logo URL
const LOGO_LIGHT_FALLBACK = 'https://customer-assets.emergentagent.com/job_bodrum-resort/artifacts/9giy6vgc_logo%203w.png';

const Footer = () => {
  const { t, currentLanguage } = useLanguage();
  const { settings } = useSettings();

  // Settings values
  const phone = settings.phone || '+90 252 382 87 00';
  const email = settings.email || 'info@hirbodrum.com';
  const reservationUrl = settings.reservation_url || 'https://www.ihg.com';
  const logoLight = settings.logo_image_light || LOGO_LIGHT_FALLBACK;
  const hotelName = settings.hotel_name || 'Holiday Inn Resort Bodrum West Beach';

  // Localized values
  const addressText = settings.address?.[currentLanguage] || settings.address?.tr || 'Turgutreis, Bahçelievler Mah. Zeyyat Mandalinci Cad. No:164, Bodrum / Muğla';
  const footerDesc = settings.footer_description?.[currentLanguage] || settings.footer_description?.tr || "Ege'nin incisi Turgutreis'te, Kos ve Kalymnos adalarına hakim eşsiz manzarası ile 5 yıldızlı Ultra Her Şey Dahil tatil deneyimi.";
  const copyrightText = settings.copyright_text?.[currentLanguage] || settings.copyright_text?.tr || `© ${new Date().getFullYear()} Holiday Inn Resort Bodrum West Beach. Tüm hakları saklıdır.`;

  const quickLinks = [
    { path: '/about', label: t('nav.about') },
    { path: '/rooms', label: t('nav.rooms') },
    { path: '/dining', label: t('nav.dining') },
    { path: '/spa', label: t('nav.spa') },
  ];

  const facilityLinks = [
    { path: '/pool', label: t('nav.pool') },
    { path: '/entertainment', label: t('nav.entertainment') },
    { path: '/kids', label: t('nav.kids') },
    { path: '/gallery', label: t('nav.gallery') },
    { path: '/contact', label: t('nav.contact') },
  ];

  const socialLinks = [
    { icon: Facebook, url: settings.social_media?.facebook || 'https://facebook.com/holidayinnbodrum', label: 'Facebook' },
    { icon: Instagram, url: settings.social_media?.instagram || 'https://instagram.com/holidayinnbodrum', label: 'Instagram' },
    { icon: Linkedin, url: settings.social_media?.linkedin || 'https://www.linkedin.com/company/holiday-inn-resort-bodrum-west-beach/', label: 'LinkedIn' },
  ];

  const phoneClean = phone.replace(/[^0-9+]/g, '');

  return (
    <footer className="bg-holiday-purple" data-testid="main-footer">
      {/* Pre-Footer CTA */}
      <div className="bg-holiday-green py-5 md:py-6">
        <div className="container-custom px-4">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="text-white text-center md:text-left">
              <p className="text-base md:text-lg font-medium">Hayalinizdeki tatil sizi bekliyor!</p>
              <p className="text-white/80 text-sm">Ultra Her Şey Dahil konseptimizle unutulmaz bir deneyim yaşayın.</p>
            </div>
            <a
              href={reservationUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-white text-holiday-green px-6 md:px-8 py-2.5 md:py-3 rounded font-semibold hover:bg-gray-100 transition-colors whitespace-nowrap text-sm md:text-base"
              data-testid="footer-cta-book"
            >
              {t('nav.bookNow')}
            </a>
          </div>
        </div>
      </div>

      {/* Main Footer */}
      <div className="container-custom px-4 py-12 md:py-16 lg:py-20">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-10 lg:gap-8">
          
          {/* Brand Column - Wider */}
          <div className="lg:col-span-2">
            {/* Logo */}
            <div className="mb-6">
              <img
                src={logoLight}
                alt={hotelName}
                className="h-14 md:h-16 w-auto object-contain"
              />
            </div>
            
            <p className="text-white/70 text-sm leading-relaxed mb-6 max-w-sm">
              {footerDesc}
            </p>

            {/* Hotel Features */}
            <div className="flex flex-wrap gap-3 mb-6">
              <div className="flex items-center gap-2 bg-white/10 px-3 py-1.5 rounded">
                <Star className="w-4 h-4 text-yellow-400" />
                <span className="text-white text-sm">5 Yıldız</span>
              </div>
            </div>

            {/* Social Media */}
            <div>
              <p className="text-white/50 text-xs uppercase tracking-wider mb-3">{t('footer.followUs')}</p>
              <div className="flex items-center gap-3">
                {socialLinks.map((social) => (
                  <a
                    key={social.label}
                    href={social.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center hover:bg-white/20 transition-all duration-300 hover:scale-105"
                    data-testid={`social-${social.label.toLowerCase()}`}
                    aria-label={social.label}
                  >
                    <social.icon size={18} className="text-white" />
                  </a>
                ))}
              </div>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-white font-semibold text-base mb-4 md:mb-5 pb-2 border-b border-white/10">
              {t('footer.quickLinks')}
            </h4>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-white/60 hover:text-white transition-colors text-sm flex items-center gap-2 group"
                    data-testid={`footer-link-${link.path.replace('/', '')}`}
                  >
                    <span className="w-1.5 h-1.5 bg-holiday-green rounded-full group-hover:scale-125 transition-transform"></span>
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Facilities */}
          <div>
            <h4 className="text-white font-semibold text-base mb-4 md:mb-5 pb-2 border-b border-white/10">
              Tesislerimiz
            </h4>
            <ul className="space-y-3">
              {facilityLinks.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-white/60 hover:text-white transition-colors text-sm flex items-center gap-2 group"
                    data-testid={`footer-facility-${link.path.replace('/', '')}`}
                  >
                    <span className="w-1.5 h-1.5 bg-holiday-green rounded-full group-hover:scale-125 transition-transform"></span>
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-white font-semibold text-base mb-4 md:mb-5 pb-2 border-b border-white/10">
              {t('footer.contact')}
            </h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <div className="w-8 h-8 bg-white/10 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                  <MapPin className="w-4 h-4 text-holiday-green" />
                </div>
                <div>
                  <p className="text-white/80 text-sm leading-relaxed whitespace-pre-line">
                    {addressText}
                  </p>
                </div>
              </li>
              <li>
                <a 
                  href={`tel:${phoneClean}`} 
                  className="flex items-center gap-3 text-white/80 hover:text-white transition-colors group"
                  data-testid="footer-phone"
                >
                  <div className="w-8 h-8 bg-white/10 rounded-lg flex items-center justify-center group-hover:bg-white/20 transition-colors">
                    <Phone className="w-4 h-4 text-holiday-green" />
                  </div>
                  <span className="text-sm">{phone}</span>
                </a>
              </li>
              <li>
                <a 
                  href={`mailto:${email}`} 
                  className="flex items-center gap-3 text-white/80 hover:text-white transition-colors group"
                  data-testid="footer-email"
                >
                  <div className="w-8 h-8 bg-white/10 rounded-lg flex items-center justify-center group-hover:bg-white/20 transition-colors">
                    <Mail className="w-4 h-4 text-holiday-green" />
                  </div>
                  <span className="text-sm">{email}</span>
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* IHG Banner */}
      <div className="border-t border-white/10">
        <div className="container-custom px-4 py-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <span className="text-white/50 text-sm">{t('footer.ihg')}</span>
              <a
                href="https://www.ihg.com"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-white/70 hover:text-white text-sm transition-colors"
                data-testid="ihg-link"
              >
                www.ihg.com
                <ExternalLink size={12} />
              </a>
            </div>
            <div className="flex items-center gap-4">
              <a
                href={reservationUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-holiday-green text-white px-5 py-2 rounded font-medium hover:bg-holiday-green-light transition-colors text-sm"
                data-testid="footer-book-now"
              >
                {t('nav.bookNow')}
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-white/10 bg-holiday-purple-dark">
        <div className="container-custom px-4 py-5">
          <div className="flex flex-col md:flex-row items-center justify-between gap-3 text-sm text-white/50">
            <p className="text-center md:text-left">{copyrightText}</p>
            <div className="flex items-center gap-4 md:gap-6">
              <Link to="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link>
              <Link to="/terms" className="hover:text-white transition-colors">Terms of Use</Link>
              <Link to="/admin/login" className="hover:text-white transition-colors">Admin</Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
